/**
 * MCP Tools Hook
 *
 * React hook that provides MCP tool execution connected to the app's stores.
 */

import { useCallback, useMemo } from 'react';
import { useIDFStore } from '../stores/idfStore';
import { useIDDStore } from '../stores/iddStore';
import { useFileStore } from '../storage';
import { useSimulationStore } from '../simulation/simulationStore';
import { createMCPExecutor, type MCPStoreAccess, type MCPFullStoreAccess, type SimulationStoreAccess } from './mcpExecutor';
import { getAllToolDefinitions } from './tools';
import type { MCPToolCall, MCPToolResult } from './types';

/**
 * Hook that provides MCP tool execution capabilities
 */
export function useMCPTools() {
  // Get IDF store state and actions
  const document = useIDFStore((state) => state.document);
  const rawContent = useIDFStore((state) => state.rawContent);
  const getObjectById = useIDFStore((state) => state.getObjectById);
  const updateField = useIDFStore((state) => state.updateField);
  const updateObject = useIDFStore((state) => state.updateObject);
  const addObject = useIDFStore((state) => state.addObject);
  const removeObject = useIDFStore((state) => state.removeObject);
  const duplicateObject = useIDFStore((state) => state.duplicateObject);

  const schema = useIDDStore((state) => state.schema);

  // Get file store state
  const currentFileName = useFileStore((state) => state.currentFileName);

  // Get simulation store state and actions
  const simStatus = useSimulationStore((state) => state.status);
  const simPhase = useSimulationStore((state) => state.phase);
  const simProgress = useSimulationStore((state) => state.progress);
  const simProgressMessage = useSimulationStore((state) => state.progressMessage);
  const simError = useSimulationStore((state) => state.error);
  const simTimeEstimate = useSimulationStore((state) => state.timeEstimate);
  const simConsoleLines = useSimulationStore((state) => state.consoleLines);
  const simOutput = useSimulationStore((state) => state.output);
  const simAvailableVariables = useSimulationStore((state) => state.availableVariables);
  const simGetTimeSeries = useSimulationStore((state) => state.getTimeSeries);
  const simWasmLoaded = useSimulationStore((state) => state.wasmLoaded);
  const simMockMode = useSimulationStore((state) => state.mockMode);
  const simStartSimulation = useSimulationStore((state) => state.startSimulation);
  const simCancelSimulation = useSimulationStore((state) => state.cancelSimulation);
  const simForceCancel = useSimulationStore((state) => state.forceCancel);
  const simSetInput = useSimulationStore((state) => state.setInput);
  const simLoadWasm = useSimulationStore((state) => state.loadWasm);
  const simInput = useSimulationStore((state) => state.input);

  // Create simulation store access object
  const simulationAccess: SimulationStoreAccess = useMemo(
    () => ({
      getStatus: () => simStatus,
      getPhase: () => simPhase,
      getProgress: () => simProgress,
      getProgressMessage: () => simProgressMessage,
      getError: () => simError,
      getTimeEstimate: () => simTimeEstimate,
      getConsoleLines: () => simConsoleLines,
      getOutput: () => simOutput,
      getAvailableVariables: () => simAvailableVariables,
      getTimeSeries: simGetTimeSeries,
      isWasmLoaded: () => simWasmLoaded,
      isMockMode: () => simMockMode,
      startSimulation: simStartSimulation,
      cancelSimulation: simCancelSimulation,
      forceCancel: simForceCancel,
      setInput: simSetInput,
      loadWasm: simLoadWasm,
      hasInput: () => simInput !== null,
      // IDF content access for prepare_simulation
      getIdfContent: () => rawContent || null,
      getIdfName: () => currentFileName,
      // EPW from existing simulation input (if user has loaded one)
      getEpwContent: () => simInput?.epw || null,
      getEpwName: () => simInput?.epwName || null,
    }),
    [
      simStatus,
      simPhase,
      simProgress,
      simProgressMessage,
      simError,
      simTimeEstimate,
      simConsoleLines,
      simOutput,
      simAvailableVariables,
      simGetTimeSeries,
      simWasmLoaded,
      simMockMode,
      simStartSimulation,
      simCancelSimulation,
      simForceCancel,
      simSetInput,
      simLoadWasm,
      simInput,
      rawContent,
      currentFileName,
    ]
  );

  // Create store access object with simulation
  const storeAccess: MCPFullStoreAccess = useMemo(
    () => ({
      getDocument: () => document,
      getObjectById,
      updateField,
      updateObject,
      addObject,
      removeObject,
      duplicateObject,
      getSchema: () => (useIDDStore.getState().isLoaded ? schema : null),
      simulation: simulationAccess,
    }),
    [document, getObjectById, updateField, updateObject, addObject, removeObject, duplicateObject, schema, simulationAccess]
  );

  // Create executor
  const executor = useMemo(() => createMCPExecutor(storeAccess), [storeAccess]);

  // Execute a single tool (sync)
  const executeTool = useCallback(
    (call: MCPToolCall): MCPToolResult => {
      return executor.execute(call);
    },
    [executor]
  );

  // Execute a single tool (async - for prepare_simulation)
  const executeToolAsync = useCallback(
    (call: MCPToolCall): Promise<MCPToolResult> => {
      return executor.executeAsync(call);
    },
    [executor]
  );

  // Execute multiple tools
  const executeTools = useCallback(
    (calls: MCPToolCall[]): MCPToolResult[] => {
      return executor.executeMany(calls);
    },
    [executor]
  );

  // Get tool definitions for Claude API (including simulation tools)
  const toolDefinitions = useMemo(() => getAllToolDefinitions(), []);

  return {
    executeTool,
    executeToolAsync,
    executeTools,
    toolDefinitions,
    isWriteTool: executor.isWriteTool,
    isSimulationTool: executor.isSimulationTool,
    isAsyncSimulationTool: executor.isAsyncSimulationTool,
    hasModel: document !== null,
    hasSchema: useIDDStore.getState().isLoaded,
    hasSimulationInput: simInput !== null,
    simulationStatus: simStatus,
  };
}

/**
 * Get store access for non-React contexts (like API routes)
 */
export function getMCPStoreAccess(): MCPStoreAccess {
  const idfStore = useIDFStore.getState();
  const iddStore = useIDDStore.getState();

  return {
    getDocument: () => idfStore.document,
    getObjectById: idfStore.getObjectById,
    updateField: idfStore.updateField,
    updateObject: idfStore.updateObject,
    addObject: idfStore.addObject,
    removeObject: idfStore.removeObject,
    duplicateObject: idfStore.duplicateObject,
    getSchema: () => (iddStore.isLoaded ? iddStore.schema : null),
  };
}

/**
 * Get full store access including simulation for non-React contexts
 */
export function getMCPFullStoreAccess(): MCPFullStoreAccess {
  const idfStore = useIDFStore.getState();
  const iddStore = useIDDStore.getState();
  const fileStore = useFileStore.getState();
  const simStore = useSimulationStore.getState();

  return {
    getDocument: () => idfStore.document,
    getObjectById: idfStore.getObjectById,
    updateField: idfStore.updateField,
    updateObject: idfStore.updateObject,
    addObject: idfStore.addObject,
    removeObject: idfStore.removeObject,
    duplicateObject: idfStore.duplicateObject,
    getSchema: () => (iddStore.isLoaded ? iddStore.schema : null),
    simulation: {
      getStatus: () => simStore.status,
      getPhase: () => simStore.phase,
      getProgress: () => simStore.progress,
      getProgressMessage: () => simStore.progressMessage,
      getError: () => simStore.error,
      getTimeEstimate: () => simStore.timeEstimate,
      getConsoleLines: () => simStore.consoleLines,
      getOutput: () => simStore.output,
      getAvailableVariables: () => simStore.availableVariables,
      getTimeSeries: simStore.getTimeSeries,
      isWasmLoaded: () => simStore.wasmLoaded,
      isMockMode: () => simStore.mockMode,
      startSimulation: simStore.startSimulation,
      cancelSimulation: simStore.cancelSimulation,
      forceCancel: simStore.forceCancel,
      setInput: simStore.setInput,
      loadWasm: simStore.loadWasm,
      hasInput: () => simStore.input !== null,
      // IDF content access for prepare_simulation
      getIdfContent: () => idfStore.rawContent || null,
      getIdfName: () => fileStore.currentFileName,
      // EPW from existing simulation input (if user has loaded one)
      getEpwContent: () => simStore.input?.epw || null,
      getEpwName: () => simStore.input?.epwName || null,
    },
  };
}
